import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import type { AccountPatch } from "../../types/account";
import { useServerAnswer } from "../../hooks/useServerAnswer";
import { RefusalSentence } from "./RefusalSentence";

interface PersonalMoveProps {
  onAnswer: (body: AccountPatch) => Promise<void>;
}

// backend/prime/accounts/personal_words.py NO_PERSONAL_ENTITY: the door goes beside it.
const NO_PERSONAL_BOOKS = "NO_PERSONAL_ENTITY";
// Requirements 1(d), revision 2 (Advocate M3): the frontend's own words, as written there,
// then the sentence 1(d) requires ("there is no reverse PATCH, which the confirmation says";
// the planner's wording, gate 2 Risk H4). The full stop closes the first sentence
// (walk-through round 1, finding 5).
const CONFIRMATION =
  "This account will keep its own books; its transactions will no longer be added to your " +
  "business books; the ones already added will be listed for you to remove.";
const CANNOT_UNDO = "This cannot be undone.";

/**
 * 1(d): "This is a personal account" as a two-step. The control opens the
 * confirmation; PATCH `purpose: personal` goes only on "Move it". The
 * no-personal-books refusal shows its sentence with the "Add personal books"
 * door (5d) beside it.
 */
export function PersonalMove({ onAnswer }: PersonalMoveProps): ReactNode {
  const [confirming, setConfirming] = useState(false);
  const { busy, refusal, run, clear } = useServerAnswer(onAnswer);

  async function move(): Promise<void> {
    if (await run({ purpose: "personal" })) setConfirming(false);
  }

  if (!confirming) {
    return (
      <button type="button" className="account-personal" onClick={() => setConfirming(true)}>
        This is a personal account
      </button>
    );
  }
  return (
    <div className="account-question">
      <p>{CONFIRMATION}</p>
      <p>{CANNOT_UNDO}</p>
      <button type="button" disabled={busy} onClick={() => void move()}>
        Move it
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={() => {
          clear();
          setConfirming(false);
        }}
      >
        Keep it in the business
      </button>
      <RefusalSentence refusal={refusal} onDismiss={clear}>
        {refusal?.code === NO_PERSONAL_BOOKS && (
          <Link to="/clients?add=personal">Add personal books</Link>
        )}
      </RefusalSentence>
    </div>
  );
}
